import { useState, useEffect } from 'react'
import Loader from "../loader/loader";
import Pagination from "../Pagination/Pagination";
import QueueFormDrawer from './QueueFormDrawer';
import { useGetQueueByIdMutation, useGetQueueListMutation, useUpdateQueueStatusMutation } from './Api';
import { FiMail, FiInbox } from "react-icons/fi";
import { FaPhone } from "react-icons/fa6";
import { BsChatDots } from "react-icons/bs";
import ToastrService from "../../services/toastService";
import Swal from "sweetalert2";

const channelMap = {
    Voice: { icon: <FaPhone size={15} />, label: "Voice", badgeClass: "bg-soft-blue text-primary" },
    Chat: { icon: <BsChatDots size={15} />, label: "Chat", badgeClass: "bg-indigo text-indigo" },
    Email: { icon: <FiMail size={15} />, label: "Email", badgeClass: "bg-warm-orange text-burnt-orange" },
};

const QUEUE_STATUSES = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
];

const QueueList = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState("add");
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    const [queueStatus, setQueueStatus] = useState(QUEUE_STATUSES[0].value);

    const [queues, setQueues] = useState([]);
    const [selectedQueue, setSelectedQueue] = useState(null);
    const [refreshQueues, setRefreshQueues] = useState(0);
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    const [getQueues, { isLoading: isQueueLoading }] = useGetQueueListMutation();
    const [getQueueById] = useGetQueueByIdMutation();
    const [updateQueueStatus] = useUpdateQueueStatusMutation();
    const [updatingQueueId, setUpdatingQueueId] = useState(null);

    const startEntry = totalRecords === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;

    const endEntry = Math.min(currentPage * rowsPerPage, totalRecords);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);


    useEffect(() => {
        const fetchQueues = async () => {
            try {
                const payload = {
                    page: currentPage,
                    limit: rowsPerPage,
                    search: debouncedSearch.trim(),
                    queueStatus,
                };

                const response = await getQueues(payload).unwrap();
                setQueues(response?.data?.queues || []);
                setTotalRecords(response?.data?.pagination?.total || 0);
                setTotalPages(response?.data?.pagination?.totalPages || 1);

            } catch (error) {
                console.error("Error fetching queues:", error);
            } finally {
                setIsFirstLoad(false);
            }
        };

        fetchQueues();
    }, [currentPage, rowsPerPage, debouncedSearch, queueStatus, refreshQueues]);


    const handleCreate = () => {
        setDrawerMode("add");
        setSelectedQueue(null);
        setIsDrawerOpen(true);
    };

    const handleQueueSaved = () => {
        setIsDrawerOpen(false);
        setRefreshQueues((prev) => prev + 1);
    };

    const handleEdit = async (queue) => {
        try {
            const payload = {
                id: queue.id
            };
            const res = await getQueueById(payload).unwrap();
            setDrawerMode("edit");
            setSelectedQueue(res?.data);
            setIsDrawerOpen(true);
        } catch (error) {
            console.error("Error fetching campaign:", error);
        }

    };

    const handleStatusChange = async (queue) => {
        const isActive = queue.status?.toLowerCase() === "active";
        const newStatus = isActive ? "inactive" : "active";

        try {
            setUpdatingQueueId(queue.id);
            const payload = {
                id: queue.id,
                status: newStatus
            };
            const res = await updateQueueStatus(payload).unwrap();
            if (res?.data?.updated === true) {
                ToastrService.success(res?.message || `Queue ${newStatus === "active" ? "activated" : "deactivated"} successfully!`);
                setRefreshQueues((prev) => prev + 1);
            } else {
                Swal.fire({
                    icon: "info",
                    title: "Cannot Deactivate",
                    text: res?.message || "Queue status cannot be updated.",
                    confirmButtonText: "OK"
                });
                // ToastrService.error(res?.message || "Failed to update queue status");
            }
        } catch (error) {
            console.error("Error updating queue status:", error);
        } finally {
            setUpdatingQueueId(null);
        }
    };

    return (
        <div>
            {isFirstLoad ? <Loader /> :
                <>
                    <div>
                        {/* Header */}
                        < div className="page-header">
                            <div className="page-header-left d-flex align-items-center">
                                <ul className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">Queue</li>
                                    <li className="breadcrumb-item active">Queue List</li>
                                </ul>
                            </div>
                        </div>

                        <div className="main-content">
                            <div className="row align-items-center mb-3">
                                <div className="col-lg-8 col-md-7">
                                    <h3 className="h4 mb-1 drawer-header">
                                        Queue List
                                    </h3>

                                    <p className="mb-0 text-muted">
                                        Manage all queues across the contact centre
                                    </p>
                                </div>

                                <div className="col-lg-4 col-md-5 mt-3 mt-md-0">
                                    <div className="d-flex justify-content-md-end">

                                        <button className="btn btn-primary px-3 py-2" onClick={handleCreate}>
                                            + Create Queue
                                        </button>
                                    </div>
                                </div>

                                <div className="card p-3 mb-4 mt-4 rounded-4">
                                    <div className="card shadow-sm border-0 p-3 mb-4">
                                        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4 gap-3">

                                            {/* Left */}
                                            <div>
                                                <h5 className="mb-1 fw-semibold">
                                                    All Queue's
                                                </h5>

                                                <small className="text-muted">
                                                    {totalRecords || 0} queues configured
                                                </small>
                                            </div>

                                            {/* Right */}
                                            <div className="d-flex gap-2 ms-lg-auto">
                                                {/* Search */}
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm campaign-search"
                                                    placeholder="Search queue name or Id..."
                                                    value={search}
                                                    onChange={(e) => {
                                                        setSearch(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                />

                                                <select className="form-select form-select-sm campaign-filter"
                                                    value={queueStatus}
                                                    onChange={(e) => {
                                                        setQueueStatus(e.target.value);
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    {QUEUE_STATUSES.map((type) => (
                                                        <option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                    </div>

                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Queue Name</th>
                                                    <th>Channel</th>
                                                    <th>Priority</th>
                                                    <th>Max Wait Time (Sec)</th>
                                                    <th>Routing Strategy</th>
                                                    <th>Overflow Queue</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isQueueLoading ? (
                                                    <tr>
                                                        <td colSpan="8" className="text-center py-5">
                                                            <div className="d-flex justify-content-center align-items-center gap-2">
                                                                <div
                                                                    className="spinner-border spinner-border-sm text-primary"
                                                                    role="status"
                                                                />

                                                                <span className="text-muted">
                                                                    Loading queues...
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : queues.length > 0 ? (
                                                    queues.map((queue) => (
                                                        <tr key={queue.id}>
                                                            <td>
                                                                <div className="fw-semibold">
                                                                    {queue.queue_name || 'N/A'}
                                                                </div>

                                                                <small className="text-muted">
                                                                    {queue.queue_code}
                                                                </small>
                                                            </td>

                                                            <td>
                                                                {queue.channel ? (
                                                                    <div className="d-flex align-items-center flex-wrap gap-1">
                                                                        {queue.channel.split(" + ").map((channel) => {
                                                                            const channelData = channelMap[channel.trim()];

                                                                            return (
                                                                                <span
                                                                                    key={channel}
                                                                                    className={`badge rounded-pill d-inline-flex align-items-center gap-1 ${channelData?.badgeClass || "bg-gray-100 text-secondary"
                                                                                        }`}
                                                                                >
                                                                                    {channelData?.icon}
                                                                                    {channel.trim()}
                                                                                </span>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>

                                                            <td>{queue.priority || 'N/A'}</td>
                                                            <td>{queue.maximum_waiting_time || 'N/A'}</td>
                                                            <td>{queue.routing_strategy || 'N/A'}</td>
                                                            <td>{queue.overflow_queue_id || 'N/A'}</td>

                                                            <td>
                                                                {queue.status ? (
                                                                    <span
                                                                        className={`badge rounded-pill ${queue.status?.toLowerCase() === "active"
                                                                            ? "bg-soft-success text-success"
                                                                            : "bg-gray-100 text-secondary"
                                                                            }`} style={{ width: "70px" }}
                                                                    >
                                                                        ● {queue.status}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted">N/A</span>
                                                                )}
                                                            </td>


                                                            <td>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <button
                                                                        className="btn btn-sm bg-soft-blue text-primary border-0 rounded-2 px-3 py-1"
                                                                        onClick={() => handleEdit(queue)}
                                                                    >
                                                                        Edit
                                                                    </button>

                                                                    {queue.status?.toLowerCase() === "active" ? (
                                                                        <button
                                                                            className="btn btn-sm bg-soft-danger text-danger border-0 rounded-2 px-3 py-1"
                                                                            onClick={() => handleStatusChange(queue)}
                                                                            disabled={updatingQueueId === queue.id}
                                                                        >
                                                                            {updatingQueueId === queue.id ? (
                                                                                <>
                                                                                    <span
                                                                                        className="spinner-border spinner-border-sm me-1"
                                                                                        role="status"
                                                                                    />
                                                                                    Updating...
                                                                                </>
                                                                            ) : (
                                                                                "Deactivate"
                                                                            )}
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            className="btn btn-sm bg-soft-success text-success border-0 rounded-2 px-3 py-1"
                                                                            onClick={() => handleStatusChange(queue)}
                                                                            disabled={updatingQueueId === queue.id}
                                                                        >
                                                                            {updatingQueueId === queue.id ? (
                                                                                <>
                                                                                    <span
                                                                                        className="spinner-border spinner-border-sm me-1"
                                                                                        role="status"
                                                                                    />
                                                                                    Updating...
                                                                                </>
                                                                            ) : (
                                                                                "Activate"
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>

                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="9" className="text-center py-5">
                                                            <div className="text-muted">
                                                                <FiInbox className="fs-1 mx-auto d-block mb-2" />

                                                                <h6 className="fw-semibold mb-1">
                                                                    No Queue found
                                                                </h6>

                                                                <small>
                                                                    {search
                                                                        ? "No queues match your search."
                                                                        : "There are no queues configured yet."}
                                                                </small>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}

                                            </tbody>
                                        </table>
                                        {/* Pagination */}

                                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">

                                            <div className="d-flex align-items-center gap-1 small">

                                                <span>Show</span>

                                                <select
                                                    className="form-select form-select-sm page-size-select"
                                                    value={rowsPerPage}
                                                    onChange={(e) => {
                                                        setRowsPerPage(Number(e.target.value));
                                                        setCurrentPage(1);
                                                    }}
                                                >
                                                    <option value={5}>5</option>
                                                    <option value={10}>10</option>
                                                    <option value={15}>15</option>
                                                </select>

                                                <span>entries</span>

                                            </div>

                                            <div className="small text-muted">
                                                Showing {startEntry} to {endEntry} of {totalRecords} entries
                                            </div>

                                            <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                maxPagesToShow={rowsPerPage}
                                                onPageChange={(page) => setCurrentPage(page)}
                                            />

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                    <QueueFormDrawer isOpen={isDrawerOpen}
                        mode={drawerMode}
                        queue={selectedQueue}
                        onSave={handleQueueSaved}
                        onClose={() => setIsDrawerOpen(false)}
                    />
                </>
            }
        </div>
    )
}

export default QueueList